/**
 * WixAppOnboarding.js — the onboarding of the Wix apps (WhatsApp AI Agent, AI Shopping Assistant, Order Tracking AI
 * Agent, Real Estate AI Agent, Instagram DM AI Automation), mounted by WixProductOnboarding for /wix/<app>/start.
 *
 * LOOK AND STEPS ARE THE MAIN WIX ONBOARDING (WixOnboarding.js / wxo-* CSS), screen for screen (owner, 04.11.2026):
 * the same purple shell with logo, language switch and step dots, one white card per step with the same widths, the
 * back button in the card's title row and the continue button below the card. WixOnboarding.js itself is untouched.
 *
 * The steps come from the backend's versioned flow (src/wix_multi/flows); this component renders:
 *   whatsapp | instagram | store | orders   the app's own first step (agent-creation/steps, drawn through `frame`)
 *   knowledge      website (the Wix site prefilled) and documents, then the radar while the agent's icon, colours,
 *                  welcome and two questions are prepared from the site (wix/onboarding/prepare_brand); the website and
 *                  documents keep learning in the background with the docked progress strips
 *   design         the widget with live preview and the suggested-question editor
 *   test           chat with the agent (in WhatsApp / Instagram look for those apps)
 *   notifications  events x channels matrix, the Wix owner's e-mail prefilled
 *   website        apps that do not add the website widget on completion (WhatsApp, Instagram): one click puts the
 *                  agent on the Wix site too (wix/apps/<app>/website), "Not now" skips
 *   plan           the plan cards with this app's own Wix prices (flow plan_prices); the checkout is the app's Wix
 *                  upgrade page in a new tab, this tab polls the agent's plan
 * Progress lives on the server (wix/apps/<app>/progress: step, draft, completion); finishing completes the
 * installation (the backend adds the website widget when the flow says so) and opens the agent in the portal.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Checkbox, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight, faArrowLeft, faArrowUp, faCheck, faXmark, faGlobe, faUpload, faTrash,
  faFileArrowUp, faFileLines, faPlus, faEllipsis, faHeadset, faEnvelope, faDesktop,
  faComments, faUserPlus, faTriangleExclamation, faBookOpen, faPuzzlePiece, faSliders,
  faMinus, faCode, faLink, faBolt, faCircleCheck, faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import ReactMarkdown from 'react-markdown';
import ChatModal from '../../components/chatModal.js';
import WixQuestionEditor from '../../components/WixQuestionEditor';
import UploadFileModal from '../../components/uploadFileModal.js';
import SlackLogo from '../../components/SlackLogo.js';
import { SlackBrandIcon, TeamsBrandIcon, TelegramBrandIcon } from '../../components/BrandIcons';
import { CHANNEL_GUIDES } from '../../data/channelGuides.js';
import { SUPPORTED_LANGUAGES } from '../../translations.js';
import { authFetch } from '../../utils/api';
import { prepareWixBrand } from '../../utils/wixPreparation';
import { wixRequest } from '../../utils/wixProductApi';
import { getCurrentOrgId, setCurrentOrgId, setRole, clearWorkspaceScopedSelections } from '../../utils/permissions';
import { fetchAgent, startCheckout } from '../agent-creation/steps';
import StepWhatsApp from '../agent-creation/steps/StepWhatsApp';
import StepInstagram from '../agent-creation/steps/StepInstagram';
import StepStore from '../agent-creation/steps/StepStore';
import StepOrders from '../agent-creation/steps/StepOrders';
import '../agent-creation/AgentCreation.css';
import '../WixOnboarding.css';
import '../WebflowOnboarding.css';
import './WixAppOnboarding.css';

const API = process.env.REACT_APP_API_BASE_URL;

// The app's own first steps; everything else is drawn here.
const APP_STEPS = { whatsapp: StepWhatsApp, instagram: StepInstagram, store: StepStore, orders: StepOrders };
// Every step this onboarding draws. A flow with another step (the old "channels" one) keeps the previous renderer.
export const WIX_APP_STEPS = new Set([...Object.keys(APP_STEPS), 'knowledge', 'design', 'test', 'notifications', 'website', 'plan']);

const FIELD_SX = {
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { border: '1px solid #515151' },
  '& .MuiInputLabel-root': { color: 'gray' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#5616ea' },
};

const DEFAULT_WIDGET = {
  themeColor: '#5616ea',
  headerColor: '#ffffff',
  hoverColor: '#4413c4',
  widgetHorizontalAlignment: 'right',
  widgetSize: 62,
  widgetBorderRadius: 50,
  headerText: 'Chat with us',
  welcomeMessage: 'Hi, how can I assist you?',
  pulsing: true,
  icon: '',   // widget launcher icon (widget_icon_path)
  logo: '',   // logo shown in the chat header (header_icon_path)
  avatar: '', // avatar shown next to agent replies (avatar_icon_path)
  predefinedQuestions: [],
};

// The radar shows at least this long, so the owner sees what is happening; the brand preparation runs meanwhile.
const SCAN_MIN_MS = 4500;
// Completion strips auto-dismiss this long after finishing, with a depleting ring.
const DONE_HIDE_MS = 5000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Card widths, the main Wix onboarding's per step; the apps' own first steps take the width of their layout.
const STAGE_WIDTH = { knowledge: 540, scanning: 640, design: 920, test: 920, notifications: 760, website: 680, plan: 1000,
  whatsapp: 760, instagram: 760, store: 920, orders: 920 };

// ── Notifications: events x channels matrix + e-mail recipients, the main onboarding's model. ──
const NOTIFY_CHANNELS = [
  { key: 'in_app', icon: faDesktop, labelKey: 'wxo_notif_ch_inapp' },
  { key: 'email', icon: faEnvelope, labelKey: 'wxo_notif_ch_email' },
];
const NOTIFY_EVENTS = [
  { key: 'new_chat', section: 'live_chat', icon: faComments, titleKey: 'wxo_notif_chat_title', descKey: 'wxo_notif_chat_desc' },
  { key: 'help_requested', section: 'live_chat', icon: faHeadset, titleKey: 'wxo_notif_help_title', descKey: 'wxo_notif_help_desc', highlight: true },
  { key: 'new_lead', section: 'leads', icon: faUserPlus, titleKey: 'wxo_notif_lead_title', descKey: 'wxo_notif_lead_desc' },
  { key: 'new_message', section: 'live_chat', icon: faEnvelope, titleKey: 'wxo_notif_msg_title', descKey: 'wxo_notif_msg_desc' },
];
const LOCKED_CHANNELS = { new_message: ['email'] }; // joined-chat replies: in-app only, no email spam
const channelLocked = (eventKey, channelKey) => (LOCKED_CHANNELS[eventKey] || []).includes(channelKey);
const DEFAULT_PREFS = {
  new_chat: { in_app: true, email: false },
  help_requested: { in_app: true, email: true },
  new_lead: { in_app: true, email: true },
  new_message: { in_app: true, email: false },
};

// ── Plan: limits from /subscription_plans (like /billing), prices the app's own Wix listing prices. ──
const TIER_ORDER = ['Smart', 'Boost', 'Ultimo'];
const FEATURED_TIER = 'Boost';
const LOGOS_SHOWN = 5;
const APPS = [
  { key: 'calendly', label: 'Calendly', logo: '/calendly-logo.png' },
  { key: 'hubspot', label: 'HubSpot', logo: '/hubspot-logo.png' },
  { key: 'stripe', label: 'Stripe', logo: '/stripe-logo.png' },
  { key: 'mailchimp', label: 'Mailchimp', logo: '/mailchimp-logo.png' },
  { key: 'calcom', label: 'Cal.com', logo: '/calcom-logo.png' },
  { key: 'zendesk', label: 'Zendesk', logo: '/zendesk-logo.png' },
];
const PRIORITY_CHANNELS = [
  { key: 'instagram', label: 'Instagram', logo: '/instagram-logo.webp' },
  { key: 'facebook', label: 'Facebook', logo: '/facebook-logo.webp' },
  { key: 'whatsapp', label: 'WhatsApp', logo: '/whatsapp-logo.webp' },
  { key: 'slack', label: 'Slack', logoComponent: SlackLogo },
  { key: 'telegram', label: 'Telegram', icon: faTelegram, color: '#229ED9' },
  { key: 'website', labelKey: 'billing_channel_website_widget', icon: faCode },
  { key: 'sharelink', labelKey: 'billing_channel_chat_link', icon: faLink },
];
const SITE_PLATFORMS = [
  { key: 'wix', label: 'Wix', logo: '/wix-logo.png' },
  { key: 'webflow', label: 'Webflow', logo: '/webflow-logo.png' },
  { key: 'wordpress', label: 'WordPress', logo: '/wordpress-logo.png' },
];
const GUIDE_CHANNELS = CHANNEL_GUIDES.map((g) => ({ key: g.key, label: g.name, logo: g.logo }));
const CHANNELS = [...PRIORITY_CHANNELS, ...SITE_PLATFORMS, ...GUIDE_CHANNELS];
const CHANNEL_ANY = { key: 'any-web', icon: faCode, labelKey: 'billing_chip_any_website', color: '#5616ea' };
const APP_ANY = { key: 'any-app', icon: faBolt, labelKey: 'billing_chip_any_app', color: '#5616ea' };
const CHANNEL_NAME = { whatsapp: 'WhatsApp', instagram: 'Instagram' };

function LogoChip({ item }) {
  const { t } = useTranslation();
  const label = item.labelKey ? t(item.labelKey) : item.label;
  return (
    <span className="rk-logo" data-tip={label} aria-label={label}>
      {item.logoComponent ? (
        <item.logoComponent size={19} />
      ) : item.logo ? (
        <img src={item.logo} alt={label} />
      ) : (
        <FontAwesomeIcon icon={item.icon} style={item.color ? { color: item.color } : undefined} />
      )}
    </span>
  );
}

// A row of the most important logos with a "+N" chip whose popover lists the hidden ones.
function LogoRow({ items, lead, max = LOGOS_SHOWN }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const shown = items.slice(0, max);
  const extra = items.length - max;
  const hidden = items.slice(max);
  const cancelClose = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const scheduleClose = () => { cancelClose(); timer.current = setTimeout(() => setOpen(false), 90); };
  useEffect(() => () => cancelClose(), []);
  return (
    <div className="rk-logo-row">
      <div className="rk-logos">
        {lead && <LogoChip key={lead.key} item={lead} />}
        {shown.map((it) => <LogoChip key={it.key} item={it} />)}
        {extra > 0 && (
          <span className="rk-logo-more-wrap">
            <button type="button" className="rk-logo-more"
              onMouseEnter={() => { cancelClose(); setOpen(true); }} onMouseLeave={scheduleClose}
              onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={t('billing_show_n_more', { count: extra })}>
              +{extra}
            </button>
            {open && (
              <div className="rk-logo-pop" role="tooltip" onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
                {hidden.map((it) => (
                  <span key={it.key} className="rk-logo-pop-item">
                    <LogoChip item={it} />
                    <span className="rk-logo-pop-label">{it.labelKey ? t(it.labelKey) : it.label}</span>
                  </span>
                ))}
              </div>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function base64ToFile(dataUrl, filename) {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8 = new Uint8Array(n);
  while (n--) u8[n] = bstr.charCodeAt(n);
  return new File([u8], filename, { type: mime });
}

// Wix customers see the Wix App Market price; without one the standard price.
function wixDisplayPrice(plan) {
  if (!plan) return 0;
  const p = plan.wix_monthly_price;
  return Number(p != null ? p : plan.monthly_price) || 0;
}

function siteHref(url) {
  const u = String(url || '').trim();
  if (!u) return '';
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}
const siteHost = (url) => String(url || '').replace(/^https?:\/\//i, '').replace(/\/$/, '');

// Any host-looking value: any TLD length, an optional scheme, port and path.
function isValidUrlSimple(value) {
  if (!value) return false;
  return /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d{2,5})?([/?#].*)?$/.test(value.trim());
}

export default function WixAppOnboarding({ appKey, state, flow, reload, openAgent }) {
  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.language || 'en').split('-')[0];
  const currentLangLabel = (SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguage) || { label: 'English' }).label;

  const steps = flow.steps;
  const index = Math.max(0, Math.min(state.step || 0, steps.length - 1));
  const key = steps[index];
  const nextKey = steps[index + 1];
  const draft = useMemo(() => state.draft || {}, [state.draft]);
  const bot = state.bot_id;
  const uid = localStorage.getItem('user_id');
  const planPrices = flow.plan_prices || null;
  const appearance = flow.chat_appearance === 'default' ? undefined : flow.chat_appearance;
  const siteUrl = siteHref(state.prefill?.site_url || '');

  const [agent, setAgent] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const handleLanguageChange = (lang) => { i18n.changeLanguage(lang); localStorage.setItem('language', lang); };

  // knowledge
  const [url, setUrl] = useState(siteUrl);
  const [docFiles, setDocFiles] = useState([]);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docSync, setDocSync] = useState({ active: false, phase: 'idle', done: 0, total: 0 });
  const [scanning, setScanning] = useState(false);
  const [scanStep, setScanStep] = useState(0);
  const built = Boolean(draft.knowledge_built);

  // background website training + documents; the business context is generated once after both finished
  const [training, setTraining] = useState({ active: false, phase: 'idle', found: 0, done: 0 });
  const docKnowledgeRef = useRef(new Set());
  const docsStartedRef = useRef(false);
  const trainingReqRef = useRef(null);
  const trainingKnowledgeRef = useRef(new Set());
  const trainedRef = useRef(false);
  const bizContextFiredRef = useRef(false);
  const srcExpectedRef = useRef(new Set());
  const srcDoneRef = useRef(new Set());

  // design
  const [widget, setWidget] = useState(DEFAULT_WIDGET);
  const dirtyWidgetRef = useRef(new Set());
  const iconInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const widgetFetchedRef = useRef(false);
  const [previewInput, setPreviewInput] = useState('');
  const [previewMsgs, setPreviewMsgs] = useState([]);
  const [previewMenuOpen, setPreviewMenuOpen] = useState(false);
  const [liveModalOpen, setLiveModalOpen] = useState(false);
  const previewBodyRef = useRef(null);
  const previewReplyRef = useRef(null);
  const previewMenuRef = useRef(null);

  // notifications
  const [notifyPrefs, setNotifyPrefs] = useState(DEFAULT_PREFS);
  const [notificationEmails, setNotificationEmails] = useState([state.prefill?.email || '']);
  const [notifySaveAttempted, setNotifySaveAttempted] = useState(false);

  // website: one click puts the agent on the Wix site ('' | 'adding' while busy)
  const [websitePhase, setWebsitePhase] = useState('');
  const [websiteError, setWebsiteError] = useState('');
  const websiteLive = Boolean(draft.website_live);

  // plan (the app's Wix upgrade page in a new tab, then poll the agent's plan)
  const [billingCycle, setBillingCycle] = useState('YEAR');
  const [isCheckingPlan, setIsCheckingPlan] = useState(false);
  const [planCheckError, setPlanCheckError] = useState('');
  const [trialStarted, setTrialStarted] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const planPollRef = useRef(null);

  // ── scroll lock + ResizeObserver-loop guard (the embedded chat preview re-measures on mount) ──
  useEffect(() => {
    const html = document.documentElement, body = document.body;
    const ph = html.style.overflow, pb = body.style.overflow;
    html.style.overflow = 'hidden'; body.style.overflow = 'hidden';
    const Native = window.ResizeObserver;
    if (Native && !Native.__wxoWrapped) {
      const Wrapped = class extends Native {
        constructor(cb) {
          super((entries, obs) => { window.requestAnimationFrame(() => { try { cb(entries, obs); } catch (_) { /* noop */ } }); });
        }
      };
      Wrapped.__wxoWrapped = true;
      window.ResizeObserver = Wrapped;
    }
    return () => {
      html.style.overflow = ph; body.style.overflow = pb;
      if (Native) window.ResizeObserver = Native;
    };
  }, []);

  useEffect(() => {
    if (!langOpen) return undefined;
    const onDown = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [langOpen]);

  // ── the installation's workspace, the brand preparation (starts early, the radar waits for it) and the agent ──
  const refreshAgent = useCallback(async () => {
    const value = await fetchAgent(bot);
    if (!value) throw new Error(t('wix_multi_agent_load_error'));
    setAgent(value);
    return value;
  }, [bot, t]);
  useEffect(() => {
    let active = true;
    (async () => {
      if (state.organization_id && String(getCurrentOrgId()) !== String(state.organization_id)) {
        const response = await fetch(`${API}orgs/switch`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ organization_id: state.organization_id }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || t('wix_multi_agent_load_error'));
        localStorage.setItem('token', data.token);
        setRole(data.role, data.owner_user_id); setCurrentOrgId(data.current_org_id);
        clearWorkspaceScopedSelections();
      }
      if (!active) return;
      prepareWixBrand(bot).catch(() => {});
      const value = await refreshAgent();
      if (active && value?.subscription_plan?.name && value.subscription_plan.name !== 'Free') setTrialStarted(true);
    })().catch((e) => active && setErr(e.message));
    return () => { active = false; };
  }, [state.organization_id, bot, refreshAgent, t]);

  // ── progress on the server; the last step completes the installation and opens the agent ──
  const move = useCallback(async (next, { complete = false, draftPatch = null } = {}) => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(true); setErr('');
    try {
      await wixRequest(appKey, 'progress', { method: 'PUT', body: JSON.stringify({
        step: next, revision: state.revision, draft: { ...draft, ...(draftPatch || {}) }, complete,
      }) });
      await reload();
      if (complete) {
        const result = await openAgent();
        if (result?.error) throw new Error(result.error);
      }
      return true;
    } catch (e) {
      setErr(e.message);
      return false;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [appKey, state.revision, draft, reload, openAgent]);
  const goNext = useCallback(() => (index === steps.length - 1 ? move(index, { complete: true }) : move(index + 1)), [index, steps.length, move]);
  const goBack = useCallback(() => (index > 0 ? move(index - 1) : null), [index, move]);
  const finish = useCallback(() => move(steps.length - 1, { complete: true }), [steps.length, move]);

  // Plan: limits and Wix prices from /subscription_plans; stop polling on unmount and when leaving the plan step.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}subscription_plans`);
        const data = await r.json();
        if (alive && Array.isArray(data)) setPlans(data);
      } catch (e) { /* best-effort */ }
      finally { if (alive) setPlansLoading(false); }
    })();
    return () => { alive = false; };
  }, []);
  useEffect(() => () => { if (planPollRef.current) clearInterval(planPollRef.current); }, []);
  useEffect(() => {
    if (key !== 'plan' && planPollRef.current) { clearInterval(planPollRef.current); planPollRef.current = null; setIsCheckingPlan(false); }
  }, [key]);

  const monthlyByName = useMemo(() => { const m = {}; plans.forEach((p) => { if (p.billing_interval !== 'year') m[p.name] = p; }); return m; }, [plans]);
  const yearlyByName = useMemo(() => { const m = {}; plans.forEach((p) => { if (p.billing_interval === 'year') m[p.name] = p; }); return m; }, [plans]);
  const pricingTiers = useMemo(() => TIER_ORDER.map((n) => monthlyByName[n]).filter(Boolean), [monthlyByName]);
  const monthlyPrice = useCallback((name) => planPrices?.[name]?.monthly ?? wixDisplayPrice(monthlyByName[name]), [planPrices, monthlyByName]);
  const yearlyPrice = useCallback((name) => planPrices?.[name]?.yearly ?? (yearlyByName[name] ? wixDisplayPrice(yearlyByName[name]) : null), [planPrices, yearlyByName]);
  const pricingSavings = useCallback((name) => {
    const mp = monthlyPrice(name); const yp = yearlyPrice(name);
    if (yp != null && mp > 0) return { pct: Math.round(((mp - yp) / mp) * 100), yearlyMonthly: yp, monthly: mp };
    return null;
  }, [monthlyPrice, yearlyPrice]);

  // ── business context: once, from the real ingested content, after every source finished ──
  const maybeGenerateBusinessContext = useCallback(() => {
    if (bizContextFiredRef.current) return;
    const expected = srcExpectedRef.current;
    if (expected.size === 0) return;
    for (const src of expected) { if (!srcDoneRef.current.has(src)) return; }
    bizContextFiredRef.current = true;
    authFetch(`${API}onboarding/generate_business_context`, { method: 'POST', body: JSON.stringify({ bot_id: bot }) })
      .catch((e) => console.error('business context generation failed', e));
  }, [bot]);
  const markSourceDone = useCallback((name) => {
    srcDoneRef.current.add(name);
    maybeGenerateBusinessContext();
  }, [maybeGenerateBusinessContext]);

  // ── one WebSocket: website training + document ingest progress ──
  useEffect(() => {
    if (!uid || !bot) return undefined;
    const raw = process.env.REACT_APP_API_WEBSOCKET_URL;
    let ws;
    try {
      if (!raw) throw new Error('WebSocket URL missing');
      ws = new WebSocket(raw.replace(/^http/, 'ws'));
    } catch (e) { console.error('WS init failed', e); return undefined; }
    ws.addEventListener('open', () => {
      ws.send(JSON.stringify({ type: 'init', user_id: uid, token: localStorage.getItem('token') }));
    });
    ws.addEventListener('message', async (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === 'find_new_links_result' && msg.request_id === trainingReqRef.current) {
        const links = (msg.new_found_links || []).slice(0, 50); // first run: at most 50 pages
        if (!links.length) {
          setTraining({ active: true, phase: 'none', found: 0, done: 0 });
          setTimeout(() => setTraining((tr) => ({ ...tr, active: false })), 2600);
          markSourceDone('website');
          return;
        }
        setTraining({ active: true, phase: 'training', found: links.length, done: 0 });
        try {
          const r = await authFetch(`${API}scrape_link`, { method: 'POST', body: JSON.stringify({ bot_id: bot, user_id: uid, urls: links }) });
          const j = await r.json();
          (j.knowledge_ids || []).forEach((id) => trainingKnowledgeRef.current.add(id));
          if (!trainingKnowledgeRef.current.size) markSourceDone('website');
        } catch (e) { console.error('scrape_link failed', e); markSourceDone('website'); }
        return;
      }
      if (msg.type === 'find_new_links_error' && msg.request_id === trainingReqRef.current) {
        setTraining({ active: true, phase: 'none', found: 0, done: 0 });
        setTimeout(() => setTraining((tr) => ({ ...tr, active: false })), 2600);
        markSourceDone('website');
        return;
      }
      if (msg.knowledge_id != null && trainingKnowledgeRef.current.has(msg.knowledge_id)) {
        if (msg.status === 'completed' || msg.status === 'failed') {
          setTraining((tr) => {
            const done = Math.min(tr.found, tr.done + 1);
            if (done >= tr.found && tr.found > 0) {
              setTimeout(() => setTraining((x) => ({ ...x, active: false })), DONE_HIDE_MS);
              markSourceDone('website');
              return { ...tr, done, phase: 'done' };
            }
            return { ...tr, done };
          });
        }
        return;
      }
      if (msg.knowledge_id != null && docKnowledgeRef.current.has(msg.knowledge_id)) {
        if (msg.status === 'completed' || msg.status === 'failed') {
          setDocSync((d) => {
            const done = Math.min(d.total, d.done + 1);
            if (done >= d.total && d.total > 0) {
              setTimeout(() => setDocSync((x) => ({ ...x, active: false })), DONE_HIDE_MS);
              markSourceDone('docs');
              return { ...d, done, phase: 'done' };
            }
            return { ...d, done };
          });
        }
      }
    });
    return () => ws.close();
  }, [uid, bot, markSourceDone]);

  // ── the widget config when entering Design (raw fetch: the global interceptor shows a modal for expected 404s) ──
  useEffect(() => {
    if (key !== 'design' || !bot || widgetFetchedRef.current) return undefined;
    widgetFetchedRef.current = true;
    let cancelled = false;
    (async () => {
      const rawFetch = window._originalFetch || fetch;
      let d = null;
      for (let i = 0; i < 15 && !cancelled; i++) {
        try {
          const res = await rawFetch(`${API}widget_configuration/${encodeURIComponent(bot)}`);
          if (res.ok) { d = await res.json(); break; }
        } catch (e) { /* keep waiting */ }
        await new Promise((r) => setTimeout(r, 1000));
      }
      if (cancelled || !d) return;
      setWidget((w) => ({
        ...w,
        themeColor: d.theme_color?.slice(0, 7) || w.themeColor,
        headerColor: d.header_font_color?.slice(0, 7) || w.headerColor,
        hoverColor: d.button_hover_color || w.hoverColor,
        pulsing: d.pulsing ?? w.pulsing,
        widgetHorizontalAlignment: d.widget_horizontal_alignment || w.widgetHorizontalAlignment,
        widgetBorderRadius: d.widget_border_radius ?? w.widgetBorderRadius,
        widgetSize: d.widget_size ?? w.widgetSize,
        headerText: d.header_text || w.headerText,
        welcomeMessage: Array.isArray(d.welcome_message) ? (d.welcome_message[0] || w.welcomeMessage) : (d.welcome_message || w.welcomeMessage),
        icon: d.widget_icon_path || '',
        logo: d.header_icon_path || '',
        avatar: d.avatar_icon_path || '',
        predefinedQuestions: Array.isArray(d.predefined_questions) ? d.predefined_questions.filter(Boolean).slice(0, 3) : [],
        // what the owner already changed here wins over a late load
        ...Object.fromEntries([...dirtyWidgetRef.current].map((k) => [k, w[k]])),
      }));
    })();
    return () => { cancelled = true; };
  }, [key, bot]);

  useEffect(() => {
    const el = previewBodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [previewMsgs]);
  useEffect(() => () => { if (previewReplyRef.current) clearTimeout(previewReplyRef.current); }, []);
  useEffect(() => {
    if (!previewMenuOpen) return undefined;
    const onDown = (e) => { if (previewMenuRef.current && !previewMenuRef.current.contains(e.target)) setPreviewMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [previewMenuOpen]);

  // cycle the radar's status line while it shows
  useEffect(() => {
    if (!scanning) { setScanStep(0); return undefined; }
    const id = setInterval(() => setScanStep((s) => (s + 1) % 4), 1300);
    return () => clearInterval(id);
  }, [scanning]);

  // ── knowledge ──
  async function startWebsiteTraining(trainUrl) {
    if (trainedRef.current) return; // one website run per onboarding
    trainedRef.current = true;
    srcExpectedRef.current.add('website');
    setTraining({ active: true, phase: 'searching', found: 0, done: 0 });
    trainingKnowledgeRef.current = new Set();
    const reqId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    trainingReqRef.current = reqId;
    try {
      await authFetch(`${API}find_new_links`, { method: 'POST', body: JSON.stringify({ bot_id: bot, url: trainUrl, request_id: reqId }) });
    } catch (e) { console.error('find_new_links failed', e); setTraining((tr) => ({ ...tr, active: false })); markSourceDone('website'); }
  }

  async function startDocumentIngest(urls) {
    if (docsStartedRef.current) return;
    docsStartedRef.current = true;
    srcExpectedRef.current.add('docs');
    setDocSync({ active: true, phase: 'processing', done: 0, total: urls.length });
    docKnowledgeRef.current = new Set();
    try {
      const r = await authFetch(`${API}ingest_document_urls`, { method: 'POST', body: JSON.stringify({ bot_id: bot, user_id: uid, document_urls: urls }) });
      const j = await r.json();
      (j.knowledge_ids || []).forEach((id) => docKnowledgeRef.current.add(id));
      if (!docKnowledgeRef.current.size) { setDocSync({ active: false, phase: 'idle', done: 0, total: 0 }); markSourceDone('docs'); }
    } catch (e) {
      console.error('ingest_document_urls failed', e);
      setDocSync({ active: false, phase: 'idle', done: 0, total: 0 });
      markSourceDone('docs');
    }
  }

  // The upload modal hands over the Azure URLs first and their metadata ([{ name, url, size }]) second; without
  // metadata the names come from the URLs.
  function handleDocsAdded(urls, files) {
    const items = (files && files.length)
      ? files
      : (urls || []).filter((u) => typeof u === 'string' && u)
        .map((u) => ({ url: u, name: decodeURIComponent((u.split('?')[0].split('/').pop()) || 'document') }));
    setDocFiles((prev) => [...prev, ...items.filter((f) => f && f.url)]);
    setDocModalOpen(false);
  }
  function removeDoc(i) { setDocFiles((prev) => prev.filter((_, idx) => idx !== i)); }

  // Build: website and documents start learning, the radar shows while the look is prepared from the site, then the
  // next step. Back on this step afterwards, the sources show read-only and the button only continues.
  async function buildKnowledge() {
    if (built) { goNext(); return; }
    setErr('');
    const trainUrl = url.trim();
    if (trainUrl && !isValidUrlSimple(trainUrl)) { setErr(t('wfo_err_url')); return; }
    if (trainUrl) startWebsiteTraining(siteHref(trainUrl));
    if (docFiles.length) startDocumentIngest(docFiles.map((f) => f.url));
    setScanning(true);
    await Promise.all([
      prepareWixBrand(bot).catch(() => {}),
      new Promise((res) => setTimeout(res, SCAN_MIN_MS)),
    ]);
    const ok = await move(index + 1, { draftPatch: { knowledge_built: true } });
    if (!ok) setScanning(false);
  }
  useEffect(() => { if (key !== 'knowledge') setScanning(false); }, [key]);

  // ── design ──
  const setStyle = (k, v) => {
    dirtyWidgetRef.current.add(k);
    setWidget((w) => ({ ...w, [k]: v }));
  };
  function handleImageUpload(e, k) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setStyle(k, ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  }
  async function uploadImage(base64) {
    const fd = new FormData();
    fd.append('file', base64ToFile(base64, `icon-${Date.now()}.png`));
    const res = await fetch(`${API}upload_image/`, { method: 'POST', body: fd });
    if (!res.ok) throw new Error('upload failed');
    const j = await res.json();
    return j.file_path;
  }
  async function saveDesignAndContinue() {
    if (busyRef.current) return;
    setErr('');
    try {
      busyRef.current = true; setBusy(true);
      let iconPath = widget.icon;
      if (iconPath && iconPath.startsWith('data:image/')) iconPath = await uploadImage(iconPath);
      let logoPath = widget.logo;
      if (logoPath && logoPath.startsWith('data:image/')) logoPath = await uploadImage(logoPath);
      let avatarPath = widget.avatar;
      if (avatarPath && avatarPath.startsWith('data:image/')) avatarPath = await uploadImage(avatarPath);
      const updates = {
        theme_color: widget.themeColor,
        header_font_color: widget.headerColor,
        button_hover_color: widget.hoverColor,
        pulsing: widget.pulsing,
        widget_horizontal_alignment: widget.widgetHorizontalAlignment,
        widget_size: widget.widgetSize,
        widget_border_radius: widget.widgetBorderRadius,
        header_text: widget.headerText,
        welcome_message: [widget.welcomeMessage],
        predefined_questions: widget.predefinedQuestions.map((q) => q.trim()).filter(Boolean).slice(0, 3),
        widget_icon_path: iconPath || '',
        header_icon_path: logoPath || '',
        avatar_icon_path: avatarPath || '',
      };
      const res = await fetch(`${API}widget_configuration/${encodeURIComponent(bot)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error('save failed');
      if (iconPath !== widget.icon) setStyle('icon', iconPath || '');
      if (logoPath !== widget.logo) setStyle('logo', logoPath || '');
      if (avatarPath !== widget.avatar) setStyle('avatar', avatarPath || '');
    } catch (e) {
      console.error(e); setErr(t('wxo_design_save_error'));
      busyRef.current = false; setBusy(false);
      return;
    }
    busyRef.current = false; setBusy(false);
    goNext();
  }

  const imageUploadControl = (k, labelKey, ref) => (
    <div className="wxo-control-group">
      <div className="wxo-color-picker-container">
        <label className="wxo-widget-setting-title">{t(labelKey)}</label>
        <div className="wxo-logo-upload">
          {widget[k] && <img className="wxo-logo-preview" src={widget[k]} alt="" />}
          <button type="button" className="wxo-logo-upload-btn" onClick={() => ref.current && ref.current.click()}>
            <FontAwesomeIcon icon={faUpload} /><span>{widget[k] ? t('wxo_design_replace') : t('wxo_design_upload')}</span>
          </button>
          {widget[k] && <button type="button" className="wxo-logo-remove-btn" onClick={() => setStyle(k, '')} aria-label="remove"><FontAwesomeIcon icon={faXmark} /></button>}
          <input ref={ref} type="file" accept="image/*" onChange={(e) => handleImageUpload(e, k)} style={{ display: 'none' }} />
        </div>
      </div>
    </div>
  );

  function sendPreview() {
    const text = previewInput.trim();
    if (!text) return;
    setPreviewMsgs((m) => [...m, { role: 'user', text }]);
    setPreviewInput('');
    if (previewReplyRef.current) clearTimeout(previewReplyRef.current);
    previewReplyRef.current = setTimeout(() => {
      setPreviewMsgs((m) => [...m, { role: 'bot', text: t('wxo_design_preview_reply') }]);
    }, 500);
  }
  function clearPreviewChat() {
    if (previewReplyRef.current) clearTimeout(previewReplyRef.current);
    setPreviewMsgs([]); setPreviewInput(''); setPreviewMenuOpen(false);
  }

  // ── notifications ──
  const notifyEmailRequired = NOTIFY_EVENTS.some((ev) => !channelLocked(ev.key, 'email') && Boolean(notifyPrefs[ev.key]?.email));
  const notifyCleanEmails = (notificationEmails || []).map((e) => String(e).trim()).filter((e) => e.length);
  const notifyInvalidEmails = notifyCleanEmails.filter((e) => !EMAIL_RE.test(e));
  const notifyEmailMissing = notifyEmailRequired && notifyCleanEmails.length === 0;
  const notifyEmailHasError = notifyEmailMissing || notifyInvalidEmails.length > 0;

  async function saveNotificationsAndContinue() {
    setNotifySaveAttempted(true);
    if (notifyEmailHasError || busyRef.current) return;
    setErr('');
    const valid = notifyCleanEmails.filter((e) => EMAIL_RE.test(e));
    const needs = (sec) => NOTIFY_EVENTS.some((ev) => ev.section === sec && !channelLocked(ev.key, 'email') && Boolean(notifyPrefs[ev.key]?.email));
    const payload = {
      prefs: notifyPrefs,
      emails_by_section: { live_chat: needs('live_chat') ? valid : [], leads: needs('leads') ? valid : [] },
      slack_webhooks_by_section: { live_chat: [], leads: [] },
      teams_webhooks_by_section: { live_chat: [], leads: [] },
      telegram_chat_ids_by_section: { live_chat: [], leads: [] },
    };
    try { await authFetch(`${API}notification_settings/${encodeURIComponent(bot)}`, { method: 'PUT', body: JSON.stringify(payload) }); }
    catch (e) { console.error('notification_settings PUT failed', e); }
    goNext();
  }

  // ── website: ONE click puts the agent on the Wix site, then the next step ──
  async function addToWebsite() {
    if (websitePhase || busyRef.current) return;
    setWebsiteError(''); setWebsitePhase('adding');
    try {
      await wixRequest(appKey, 'website', { method: 'POST' });
    } catch (e) {
      console.error('Wix website widget failed:', e);
      // 503: this Wix app has no website extension yet; 403: site changes are switched off on this server.
      setWebsiteError(e.status === 503 ? t('wap_website_unavailable') : e.status === 403 ? e.message : t('wap_website_error'));
      setWebsitePhase('');
      return;
    }
    setWebsitePhase('');
    await move(index + 1, { draftPatch: { website_live: true } });
  }

  // ── plan: the app's Wix upgrade page in a NEW TAB, this tab polls the agent's plan until it is no longer "Free" ──
  function stopPlanPolling() {
    if (planPollRef.current) { clearInterval(planPollRef.current); planPollRef.current = null; }
    setIsCheckingPlan(false);
  }
  async function fetchBotPlanName() {
    const r = await fetch(`${API}bot?bot_id=${encodeURIComponent(bot)}&include_plan=true`);
    if (!r.ok) throw new Error('bot fetch failed');
    const j = await r.json();
    return j?.subscription_plan?.name ?? 'Free';
  }
  function startPlanPolling() {
    if (planPollRef.current) return;
    setPlanCheckError('');
    setIsCheckingPlan(true);
    planPollRef.current = setInterval(async () => {
      try {
        const name = await fetchBotPlanName();
        if (name && name !== 'Free') { stopPlanPolling(); setTrialStarted(true); }
      } catch (e) { setPlanCheckError(t('wxo_price_still_checking')); }
    }, 2000);
  }
  async function startTrialCheckout(row) {
    setErr('');
    if (state.upgrade_url) {
      window.open(state.upgrade_url, '_blank', 'noopener,noreferrer');
      startPlanPolling();
      return;
    }
    // No upgrade address in the state (older backend): the shared checkout, which also opens the app's Wix page.
    try { await startCheckout({ agent: agent || { id: bot, wix_product: true }, plan: row, returnPath: `/wix/${appKey}/start` }); }
    catch (e) { console.error('checkout failed', e); setErr(t('billing_checkout_start_error')); }
  }

  // ── progress strips ──
  const doneTimer = () => (
    <span className="wxo-strip-timer" aria-hidden="true">
      <svg viewBox="0 0 24 24" className="wxo-strip-timer-svg">
        <circle className="wxo-strip-timer-track" cx="12" cy="12" r="10" />
        <circle className="wxo-strip-timer-ring" cx="12" cy="12" r="10" style={{ animationDuration: `${DONE_HIDE_MS}ms` }} />
      </svg>
      <FontAwesomeIcon icon={faCheck} className="wxo-strip-timer-check" />
    </span>
  );
  const trainPct = Math.round((training.done / Math.max(1, training.found)) * 100);
  const trainStrip = training.active ? (
    <div className="wxo-train-strip">
      <div className="wxo-train-inner">
        {training.phase === 'searching' && (<><span className="wxo-train-spin" /><span className="wxo-train-label">{t('wxo_train_searching')}</span></>)}
        {training.phase === 'training' && (
          <div className="wxo-train-prog">
            <div className="wxo-train-row">
              <span className="wxo-train-label">{t('wxo_train_progress', { done: training.done, total: training.found })}</span>
              <span className="wxo-train-pct">{trainPct}%</span>
            </div>
            <div className="wxo-train-bar"><div className="wxo-train-fill" style={{ width: `${trainPct}%` }} /></div>
          </div>
        )}
        {training.phase === 'done' && (<>{doneTimer()}<span className="wxo-train-label">{t('wxo_train_done', { count: training.found })}</span></>)}
        {training.phase === 'none' && (<span className="wxo-train-label">{t('wxo_train_none')}</span>)}
      </div>
    </div>
  ) : null;
  const docPct = Math.round((docSync.done / Math.max(1, docSync.total)) * 100);
  const docStrip = docSync.active ? (
    <div className="wxo-train-strip">
      <div className="wxo-train-inner">
        {docSync.phase === 'processing' ? (
          <div className="wxo-train-prog">
            <div className="wxo-train-row">
              <span className="wxo-train-label">{t('wxo_docs_processing', { done: docSync.done, total: docSync.total })}</span>
              <span className="wxo-train-pct">{docPct}%</span>
            </div>
            <div className="wxo-train-bar"><div className="wxo-train-fill" style={{ width: `${docPct}%` }} /></div>
          </div>
        ) : (<>{doneTimer()}<span className="wxo-train-label">{t('wxo_docs_done', { count: docSync.total })}</span></>)}
      </div>
    </div>
  ) : null;
  const progressStrips = (training.active || docSync.active) ? (<>{trainStrip}{docStrip}</>) : null;

  // ── render helpers ──
  const backBtn = index > 0 && !scanning ? (
    <button className="wxo-back-inline" type="button" aria-label={t('wxo_back')} onClick={goBack} disabled={busy}>
      <FontAwesomeIcon icon={faArrowLeft} /><span>{t('wxo_back')}</span>
    </button>
  ) : null;
  const titleRow = (heading) => <div className="wxo-title-row">{backBtn}{heading}</div>;

  // The app's own first step in this look: its content in the white card, its button below the card.
  // fixedHeader: the heading and its subline stay put, only the step's own scroll area inside the body scrolls.
  const appFrame = ({ title, titleIcon = null, sub, bodyClass = '', children, primary, skip = null, hint = null, fixedHeader = false }) => (
    <>
      <div className={`wxo-card wap-app-card ${fixedHeader ? 'wap-card-fixed' : ''}`}>
        {titleRow(<h2 className="wxo-h2 wap-app-title">{titleIcon && <span className="wap-app-title-icon">{titleIcon}</span>}{title}</h2>)}
        {sub && <p className="wxo-sub">{sub}</p>}
        <div className={`wap-app-body ${bodyClass}`}>{children}</div>
        {hint && <p className="wap-app-hint">{hint}</p>}
        {err && <div className="wxo-err">{err}</div>}
      </div>
      <div className="wxo-stage-cta">
        <button type="button" className="wxo-cta-below" onClick={primary.onClick} disabled={primary.disabled || primary.busy || busy}>
          <span>{primary.label}</span>
          {primary.busy || busy ? <span className="wxo-spin" /> : <FontAwesomeIcon icon={faArrowRight} />}
        </button>
      </div>
      {skip && <p className="wap-below-link"><button type="button" onClick={skip.onClick} disabled={busy}>{skip.label}</button></p>}
    </>
  );

  const langToggle = (
    <div className="wxo-lang" ref={langRef}>
      <button type="button" className="wxo-lang-trigger" onClick={() => setLangOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={langOpen}>
        <FontAwesomeIcon icon={faGlobe} className="wxo-lang-icon" />
        <span className="wxo-lang-current">{currentLangLabel}</span>
      </button>
      {langOpen && (
        <div className="wxo-lang-menu" role="listbox">
          {SUPPORTED_LANGUAGES.map((l) => {
            const sel = l.code === currentLanguage;
            return (
              <button key={l.code} type="button" role="option" aria-selected={sel} className={`wxo-lang-option ${sel ? 'wxo-sel' : ''}`}
                onClick={() => { handleLanguageChange(l.code); setLangOpen(false); }}>
                <span className="wxo-lang-check">{sel && <FontAwesomeIcon icon={faCheck} />}</span>{l.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // One card at a time; its width snaps per step (the fade-in masks it), the anim key replays the entrance.
  const stageW = key === 'knowledge' && scanning ? STAGE_WIDTH.scanning : (STAGE_WIDTH[key] || 760);
  const animKey = key === 'knowledge' ? `k-${scanning ? 'scan' : 'input'}` : `s-${index}-${key}`;

  // The primary CTA renders BELOW the card on the purple shell; the plan keeps its in-card buttons.
  let stageCta = null;
  if (key === 'knowledge' && !scanning) stageCta = { label: built ? t('wxo_continue') : t('wxo_build_agent'), onClick: buildKnowledge };
  else if (key === 'design') stageCta = { label: nextKey === 'test' ? t('wxo_to_test') : t('wxo_continue'), onClick: saveDesignAndContinue };
  else if (key === 'test') stageCta = { label: t('wxo_test_continue'), onClick: goNext };
  else if (key === 'notifications') stageCta = { label: t('wxo_notif_continue'), onClick: saveNotificationsAndContinue };
  else if (key === 'website') stageCta = websiteLive
    ? { label: t('wxo_continue'), onClick: goNext }
    : { label: t('wap_website_button'), onClick: addToWebsite, busy: Boolean(websitePhase) };
  const AppStep = APP_STEPS[key];
  const hasCta = Boolean(stageCta) || Boolean(AppStep);

  return (
    <div className="wxo-scope wxo-wrapper wfo-wrapper wap-wrapper">
      <img className="wxo-logo" src="/ultimo-bots-white.png" alt="Ultimo Bots" />
      {langToggle}
      <div className="wxo-step-ind">
        {/* the account comes first (done), then the flow's steps */}
        {Array.from({ length: steps.length + 1 }).map((_, i) => (
          <span key={i} className={`wxo-step-dot ${i === index + 1 ? 'wxo-current' : i < index + 1 ? 'wxo-done' : ''}`} />
        ))}
      </div>

      <div className={`wxo-stage ${hasCta ? 'wxo-has-cta' : ''}`} style={{ maxWidth: stageW }}>
        <div className="wxo-card-anim" key={animKey}>
          {!agent ? (
            <div className="wxo-card"><div className="wxo-trial-wait"><div className="wxo-scan-ring" />{err && <div className="wxo-err">{err}</div>}</div></div>
          ) : (
            <>
              {/* ── the app's own first step ── */}
              {AppStep && (
                <AppStep agent={agent} botId={bot} goNext={goNext} busy={busy} returnPath={`/wix/${appKey}/start`}
                  prefillEmail={state.prefill?.email || ''} frame={appFrame} />
              )}

              {/* ── Knowledge, then the radar while the look is prepared ── */}
              {key === 'knowledge' && !scanning && (
                <div className="wxo-card">
                  {titleRow(<h2 className="wxo-h2">{t('wxo_connect_title')}</h2>)}
                  <p className="wxo-sub">{t('wfo_connect_sub')}</p>
                  {siteUrl && (
                    <div className="wxo-site-banner">
                      <span className="wxo-site-dot" />
                      <span className="wxo-site-banner-text">{t('wxo_site_connected_to')} <strong>{siteHost(siteUrl)}</strong></span>
                    </div>
                  )}
                  <div className="wxo-sources">
                    <div className="wxo-source">
                      <div className="wxo-source-top">
                        <span className="wxo-source-ic"><FontAwesomeIcon icon={faGlobe} /></span>
                        <span className="wxo-source-label">{t('wxo_src_website')}</span>
                      </div>
                      <TextField label={t('wxo_url_placeholder')} type="text" value={url} onChange={(e) => setUrl(e.target.value)}
                        fullWidth variant="outlined" size="small" disabled={built} sx={FIELD_SX} />
                    </div>
                    <div className={`wxo-source wxo-source-docs ${docFiles.length ? 'wxo-has' : ''}`} role="button" tabIndex={0}
                      onClick={() => !built && setDocModalOpen(true)}
                      onKeyDown={(e) => { if (!built && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setDocModalOpen(true); } }}>
                      <div className="wxo-source-top">
                        <span className="wxo-source-ic"><FontAwesomeIcon icon={faFileArrowUp} /></span>
                        <span className="wxo-source-label">{t('wxo_src_documents')}</span>
                        {docFiles.length > 0 && !built
                          ? <button type="button" className="wxo-source-clear" onClick={(e) => { e.stopPropagation(); setDocFiles([]); }}>{t('wxo_docs_clear')}</button>
                          : <FontAwesomeIcon icon={faArrowRight} className="wxo-source-arrow" />}
                      </div>
                      {docFiles.length > 0 ? (
                        <>
                          <ul className="wxo-doc-list">
                            {docFiles.map((f, i) => (
                              <li key={`${f.url}-${i}`} className="wxo-doc-item">
                                <FontAwesomeIcon icon={faFileLines} className="wxo-doc-ic" />
                                <span className="wxo-doc-name" title={f.name}>{f.name}</span>
                                {!built && <button type="button" className="wxo-doc-rm" onClick={(e) => { e.stopPropagation(); removeDoc(i); }} aria-label={t('wxo_docs_remove')}><FontAwesomeIcon icon={faXmark} /></button>}
                              </li>
                            ))}
                          </ul>
                          {!built && <span className="wxo-doc-more"><FontAwesomeIcon icon={faPlus} /> {t('wxo_docs_more')}</span>}
                        </>
                      ) : (<span className="wxo-source-hint">{t('wxo_docs_hint')}</span>)}
                    </div>
                  </div>
                  {err && <div className="wxo-err">{err}</div>}
                  <p className="wxo-privacy">{t('wxo_privacy')}</p>
                </div>
              )}

              {key === 'knowledge' && scanning && (
                <div className="wxo-card wxo-card-skills">
                  <div className="wxo-skills-stage">
                    <div className="wxo-skills-grid-bg" aria-hidden="true" />
                    <div className="wxo-skills-glow" aria-hidden="true" />
                    <div className="wxo-scan2">
                      <div className="wxo-radar" aria-hidden="true">
                        <span className="wxo-radar-ring" />
                        <span className="wxo-radar-ring wxo-radar-ring-2" />
                        <span className="wxo-radar-ring wxo-radar-ring-3" />
                        <span className="wxo-radar-sweep" />
                        <span className="wxo-radar-core"><img src="/wix-logo.png" alt="" /></span>
                      </div>
                      <h2 className="wxo-skills-h2">{t('wxo_scan_title')}</h2>
                      <div className="wxo-scan-status">
                        <span className="wxo-scan-pulse" />
                        <span className="wxo-scan-status-text" key={scanStep}>{t(`wfo_scan_step_${scanStep + 1}`)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Design ── */}
              {key === 'design' && (
                <div className="wxo-card">
                  {titleRow(<h2 className="wxo-h2">{t('wxo_design_title')}</h2>)}
                  <p className="wxo-sub">{t('wxo_design_sub')}</p>
                  <div className="wxo-preview-card">
                    <div className="wxo-user-settings">
                      <div className="wxo-control-group">
                        <TextField label={t('wxo_design_header')} value={widget.headerText} onChange={(e) => setStyle('headerText', e.target.value)}
                          fullWidth variant="outlined" inputProps={{ maxLength: 60 }} helperText={`${(widget.headerText || '').length}/60`} sx={FIELD_SX} />
                      </div>
                      <div className="wxo-control-group">
                        <TextField label={t('wxo_design_welcome')} value={widget.welcomeMessage} onChange={(e) => setStyle('welcomeMessage', e.target.value)}
                          fullWidth multiline minRows={2} maxRows={4} variant="outlined" inputProps={{ maxLength: 300 }}
                          helperText={`${(widget.welcomeMessage || '').length}/300`} sx={FIELD_SX} />
                      </div>
                      <div className="wxo-control-group">
                        <div className="wxo-color-picker-container">
                          <label className="wxo-widget-setting-title">{t('wxo_design_theme')}</label>
                          <input type="color" value={widget.themeColor} onChange={(e) => setStyle('themeColor', e.target.value)} />
                        </div>
                      </div>
                      <div className="wxo-control-group">
                        <div className="wxo-color-picker-container">
                          <label className="wxo-widget-setting-title">{t('wxo_design_header_color')}</label>
                          <input type="color" value={widget.headerColor} onChange={(e) => setStyle('headerColor', e.target.value)} />
                        </div>
                      </div>
                      {imageUploadControl('icon', 'wxo_design_icon', iconInputRef)}
                      {imageUploadControl('logo', 'wxo_design_logo', logoInputRef)}
                      {imageUploadControl('avatar', 'wxo_design_avatar', avatarInputRef)}
                    </div>

                    <div className="wxo-divider" />

                    <div className="wxo-preview-area">
                      <div className="wxo-chat-bubble" style={{ background: widget.icon ? 'transparent' : widget.themeColor }}>
                        {widget.icon ? <img src={widget.icon} alt="" /> : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="58%" viewBox="0 0 24 24" fill="#fff"><path fillRule="evenodd" d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 0 0 1.28.53l4.184-4.183a.39.39 0 0 1 .266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0 0 12 2.25ZM8.25 8.625a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Zm2.625 1.125a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z" clipRule="evenodd" /></svg>
                        )}
                      </div>
                      <div className="wxo-chat-window">
                        <div className="wxo-chat-header" style={{ backgroundColor: widget.themeColor }}>
                          <div className="wxo-chat-header-title">
                            <span className="wxo-chat-header-brand">
                              {widget.logo && <img className="wxo-chat-logo" src={widget.logo} alt="" />}
                              <span className="wxo-chat-title" style={{ color: widget.headerColor }}>{widget.headerText || 'Chat with us'}</span>
                            </span>
                            <span className="wxo-chat-actions" ref={previewMenuRef}>
                              <button type="button" className="wxo-chat-kebab" style={{ color: widget.headerColor }}
                                aria-label={t('wxo_design_menu_more')} aria-haspopup="menu" aria-expanded={previewMenuOpen}
                                onClick={() => setPreviewMenuOpen((o) => !o)}>
                                <FontAwesomeIcon icon={faEllipsis} />
                              </button>
                              <button type="button" className="wxo-chat-close" style={{ color: widget.headerColor }} aria-label="close"><FontAwesomeIcon icon={faXmark} /></button>
                              {previewMenuOpen && (
                                <div className="wxo-chat-menu" role="menu">
                                  <button type="button" className="wxo-chat-menu-item" role="menuitem" onClick={clearPreviewChat}>
                                    <FontAwesomeIcon icon={faTrash} /> {t('wxo_design_menu_clear')}
                                  </button>
                                  <button type="button" className="wxo-chat-menu-item" role="menuitem" onClick={() => { setPreviewMenuOpen(false); setLiveModalOpen(true); }}>
                                    <FontAwesomeIcon icon={faHeadset} /> {t('wxo_design_menu_human')}
                                  </button>
                                </div>
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="wxo-chat-body" ref={previewBodyRef}>
                          <div className="wxo-chat-row bot">
                            {widget.avatar && <img className="wxo-chat-avatar" src={widget.avatar} alt="" />}
                            <div className="wxo-chat-msg">{widget.welcomeMessage || 'Hi, how can I assist you?'}</div>
                          </div>
                          {previewMsgs.map((m, i) => (
                            <div key={i} className={`wxo-chat-row ${m.role}`}>
                              {m.role === 'bot' && widget.avatar && <img className="wxo-chat-avatar" src={widget.avatar} alt="" />}
                              <div className={`wxo-chat-msg ${m.role === 'user' ? 'wxo-chat-msg-user' : 'wxo-chat-msg-md'}`}
                                style={m.role === 'user' ? { background: widget.themeColor, borderColor: widget.themeColor } : undefined}>
                                {m.role === 'bot' ? <ReactMarkdown>{m.text}</ReactMarkdown> : m.text}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="wxo-chat-footer">
                          <WixQuestionEditor questions={widget.predefinedQuestions} onChange={(questions) => setStyle('predefinedQuestions', questions)} />
                          <div className="wxo-live-cta-row">
                            <button type="button" className="wxo-live-cta-btn" onClick={() => setLiveModalOpen(true)}>
                              <FontAwesomeIcon icon={faHeadset} /><span>{t('wxo_design_menu_human')}</span>
                            </button>
                          </div>
                          <div className="wxo-powered"><span className="wxo-powered-text">Powered by Ultimo Bots</span></div>
                          <div className="wxo-input-send">
                            <input className="wxo-chat-input" type="text" value={previewInput} placeholder={t('chat_input_placeholder')}
                              onChange={(e) => setPreviewInput(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendPreview(); } }} />
                            <button type="button" className="wxo-chat-send" style={{ backgroundColor: widget.themeColor, color: widget.headerColor }}
                              onClick={sendPreview} disabled={!previewInput.trim()}><FontAwesomeIcon icon={faArrowUp} /></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {err && <div className="wxo-err">{err}</div>}
                  {progressStrips}
                </div>
              )}

              {/* ── Test ── */}
              {key === 'test' && (
                <div className="wxo-card wxo-card-test">
                  {titleRow(<h2 className="wxo-h2">{t('wxo_test_title')}</h2>)}
                  <p className="wxo-sub">{t('wpo_test_sub')}</p>
                  <div className="wxo-test-chat">
                    <ChatModal embedded open onClose={() => {}} appearance={appearance} displayName={agent?.name}
                      bot={{ id: bot, knowledge_count: 1 }} currentMessageCount={0} messagePlanLimit={999999} planName="trial"
                      openSubscriptionModalFromChat={() => {}} onMessageCountChange={() => {}}
                      liveCtaAlways onLiveCta={() => setLiveModalOpen(true)} showImprove={false} />
                  </div>
                  {err && <div className="wxo-err">{err}</div>}
                  {progressStrips}
                </div>
              )}

              {/* ── Notifications ── */}
              {key === 'notifications' && (
                <div className="wxo-card">
                  {titleRow(<h2 className="wxo-h2">{t('wxo_notif_title')}</h2>)}
                  <p className="wxo-sub">{t('wxo_notif_sub')}</p>
                  <div className="wxo-notify-matrix">
                    <div className="wxo-notify-row wxo-notify-head">
                      <div className="wxo-notify-event-col" />
                      {NOTIFY_CHANNELS.map((ch) => (
                        <div key={ch.key} className="wxo-notify-channel-col">
                          <FontAwesomeIcon icon={ch.icon} className="wxo-notify-channel-icon" />
                          <span className="wxo-notify-channel-label">{t(ch.labelKey)}</span>
                        </div>
                      ))}
                    </div>
                    {NOTIFY_EVENTS.map((ev) => {
                      const row = notifyPrefs[ev.key] || {};
                      return (
                        <div key={ev.key} className={`wxo-notify-row ${ev.highlight ? 'wxo-notify-row-hi' : ''}`}>
                          <div className="wxo-notify-event-col">
                            <div className="wxo-notify-event-title"><FontAwesomeIcon icon={ev.icon} className="wxo-notify-event-icon" />{t(ev.titleKey)}</div>
                            <div className="wxo-notify-event-desc">{t(ev.descKey)}</div>
                          </div>
                          {NOTIFY_CHANNELS.map((ch) => {
                            const locked = channelLocked(ev.key, ch.key);
                            const checkbox = (
                              <Checkbox checked={locked ? false : Boolean(row[ch.key])} disabled={locked}
                                onChange={() => {
                                  setNotifySaveAttempted(false);
                                  setNotifyPrefs((prev) => ({ ...prev, [ev.key]: { ...(prev[ev.key] || {}), [ch.key]: !prev[ev.key]?.[ch.key] } }));
                                }}
                                sx={{ '&.Mui-checked': { color: '#5616ea' }, '&:hover': { backgroundColor: '#5616ea15' }, '&.Mui-disabled': { color: '#e0e0e6' }, padding: '4px' }} />
                            );
                            return (
                              <div key={ch.key} className="wxo-notify-channel-col">
                                {locked ? (
                                  <Tooltip title={t('wxo_notif_locked')} arrow><span style={{ cursor: 'not-allowed', display: 'inline-flex' }}><span style={{ pointerEvents: 'none' }}>{checkbox}</span></span></Tooltip>
                                ) : checkbox}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                  <div className="wxo-notify-portal">
                    <span className="wxo-notify-portal-label">{t('wxo_notif_also')}</span>
                    <span className="wxo-notify-chips">
                      <span className="wxo-notify-chip"><TelegramBrandIcon size={15} />Telegram</span>
                      <span className="wxo-notify-chip"><SlackBrandIcon size={15} />Slack</span>
                      <span className="wxo-notify-chip"><TeamsBrandIcon size={15} />Teams<span className="wxo-notify-soon">{t('wxo_notif_soon')}</span></span>
                    </span>
                  </div>
                  {notifyEmailRequired && (
                    <div className="wxo-notify-recipients">
                      <div className="wxo-notify-recipients-head"><FontAwesomeIcon icon={faEnvelope} style={{ color: '#5616ea' }} /><span>{t('wxo_notif_recipients')}</span></div>
                      <div className="wxo-notify-emails">
                        {(notificationEmails.length ? notificationEmails : ['']).map((value, idx) => {
                          const list = notificationEmails.length ? notificationEmails : [''];
                          const trimmed = String(value).trim();
                          const showError = notifySaveAttempted && trimmed.length > 0 && !EMAIL_RE.test(trimmed);
                          return (
                            <div key={idx} className="wxo-notify-email-row">
                              <TextField label={t('wxo_notif_email_n', { n: idx + 1 })} value={value}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setNotifySaveAttempted(false);
                                  setNotificationEmails((prev) => { const next = prev.slice(); next[idx] = v; return next; });
                                }}
                                type="text" fullWidth variant="outlined" size="small" error={showError} placeholder="you@example.com"
                                InputProps={{ endAdornment: list.length > 1 ? (<InputAdornment position="end"><IconButton aria-label="Remove" edge="end" size="small" onClick={() => { setNotifySaveAttempted(false); setNotificationEmails((prev) => { const next = prev.filter((_, i) => i !== idx); return next.length ? next : ['']; }); }}><FontAwesomeIcon icon={faTrash} style={{ fontSize: 13 }} /></IconButton></InputAdornment>) : null }}
                                sx={FIELD_SX} />
                            </div>
                          );
                        })}
                      </div>
                      {notificationEmails.length < 3 && (
                        <button type="button" className="wxo-notify-add-email" onClick={() => { setNotifySaveAttempted(false); setNotificationEmails((prev) => (prev.length >= 3 ? prev : [...prev, ''])); }} title={t('wxo_notif_add_email')}>
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                      )}
                      {notifySaveAttempted && notifyEmailHasError && (
                        <div className="wxo-notify-error"><FontAwesomeIcon icon={faTriangleExclamation} /><span>{notifyEmailMissing ? t('wxo_notif_err_missing') : t('wxo_notif_err_invalid')}</span></div>
                      )}
                    </div>
                  )}
                  {err && <div className="wxo-err">{err}</div>}
                </div>
              )}

              {/* ── Website: also on the Wix site, one click ── */}
              {key === 'website' && (
                <div className="wxo-card">
                  {titleRow(<h2 className="wxo-h2">{t('wap_website_title')}</h2>)}
                  <p className="wxo-sub">{t('wap_website_text', { channel: CHANNEL_NAME[flow.chat_appearance] || 'WhatsApp' })}</p>
                  <div className={`wfo-add-site ${websiteLive ? 'wfo-added' : ''}`}>
                    <span className="wfo-add-logo"><img src="/wix-logo.png" alt="" /></span>
                    <span className="wfo-add-text">
                      <strong>{siteHost(siteUrl) || t('wap_website_your_site')}</strong>
                      <span>{t('wap_website_site_hint')}</span>
                    </span>
                    {websiteLive && <span className="wfo-add-done" role="status"><FontAwesomeIcon icon={faCircleCheck} /> {t('wfo_live')}</span>}
                    {!websiteLive && websitePhase && (
                      <span className="wfo-add-busy" role="status"><FontAwesomeIcon icon={faSpinner} spin /> {t('wap_website_busy')}</span>
                    )}
                  </div>
                  {websiteError && <div className="wxo-err" role="alert">{websiteError}</div>}
                  {err && <div className="wxo-err">{err}</div>}
                  {progressStrips}
                </div>
              )}

              {/* ── Plan: the app's own Wix prices; checkout on the app's Wix page ── */}
              {key === 'plan' && (
                <div className="wxo-card wxo-pricing">
                  {trialStarted ? (
                    <div className="wxo-trial-done">
                      <div className="wxo-trial-done-ic"><FontAwesomeIcon icon={faCheck} /></div>
                      <h2 className="wxo-h2">{t('wxo_price_done_title')}</h2>
                      <p className="wxo-sub">{t('wxo_price_done_sub')}</p>
                      {err && <div className="wxo-err">{err}</div>}
                      <button className="wxo-btn" onClick={finish} disabled={busy}>{t('wxo_price_to_portal')} {busy ? <span className="wxo-spin" /> : <FontAwesomeIcon icon={faArrowRight} />}</button>
                    </div>
                  ) : isCheckingPlan ? (
                    <div className="wxo-trial-wait">
                      <div className="wxo-scan-ring" />
                      <h2 className="wxo-h2">{t('wxo_price_detecting_title')}</h2>
                      <p className="wxo-sub">{t('wxo_price_detecting_sub')}</p>
                      {planCheckError && <div className="wxo-err">{planCheckError}</div>}
                      <div className="wxo-trial-wait-actions">
                        <button className="wxo-btn-ghost" onClick={stopPlanPolling}>{t('wxo_price_back')}</button>
                        <button className="wxo-btn" onClick={async () => { try { const n = await fetchBotPlanName(); if (n !== 'Free') { stopPlanPolling(); setTrialStarted(true); } else { setPlanCheckError(t('wxo_price_still_checking')); } } catch { setPlanCheckError(t('wxo_price_still_checking')); } }}>{t('wxo_price_refresh')}</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {titleRow(<h2 className="wxo-h2">{t('wxo_price_title')}</h2>)}
                      <div className="rk-billing-toggle">
                        <span className={`rk-billing-thumb${billingCycle === 'YEAR' ? ' right' : ''}`} aria-hidden />
                        <button type="button" className={billingCycle === 'MONTH' ? 'active' : ''} onClick={() => setBillingCycle('MONTH')}>{t('monthly')}</button>
                        <button type="button" className={billingCycle === 'YEAR' ? 'active' : ''} onClick={() => setBillingCycle('YEAR')}>{t('yearly')}</button>
                      </div>
                      {plansLoading && !pricingTiers.length ? (
                        <div className="wxo-trial-wait"><div className="wxo-scan-ring" /></div>
                      ) : (
                        <div className="rk-plans">
                          {pricingTiers.map((mRow) => {
                            const name = mRow.name;
                            const sv = pricingSavings(name);
                            const featured = name === FEATURED_TIER;
                            const rescrape = mRow.automatic_rescraping;
                            const removeBranding = !!mRow.remove_branding;
                            return (
                              <div key={name} className={`rk-plan${featured ? ' featured' : ''}`}>
                                {featured && <span className="rk-plan-flag">{t('recommended_start')}</span>}
                                <div className="rk-plan-name">{name}</div>
                                {billingCycle === 'YEAR' && sv ? (
                                  <div>
                                    <div className="rk-plan-price">
                                      <s className="rk-plan-old">${sv.monthly}</s>
                                      <span className="rk-plan-arrow">→</span>
                                      ${sv.yearlyMonthly}
                                      <span className="rk-plan-save">−{sv.pct}%</span>
                                    </div>
                                    <div className="rk-plan-billed">{t('billing_per_month_billed_annually')}</div>
                                  </div>
                                ) : (
                                  <div className="rk-plan-price">${monthlyPrice(name)}<span>{t('wxo_price_per_month')}</span></div>
                                )}
                                <div className="rk-plan-limits">
                                  <div className="rk-plan-limit"><FontAwesomeIcon icon={faComments} /><span>{t('wxo_price_limit_messages')}</span><strong>{(mRow.message_limit ?? 0).toLocaleString('de-CH')}</strong></div>
                                  <div className="rk-plan-limit"><FontAwesomeIcon icon={faBookOpen} /><span>{t('wxo_price_limit_knowledge')}</span><strong>{(mRow.document_limit ?? 0).toLocaleString('de-CH')}</strong></div>
                                  <div className="rk-plan-limit"><FontAwesomeIcon icon={faPuzzlePiece} /><span>{t('wxo_price_limit_skills')}</span><strong>{mRow.skill_limit ?? 0}</strong></div>
                                  <div className="rk-plan-limit"><FontAwesomeIcon icon={faSliders} /><span>{t('wxo_price_limit_behaviors')}</span><strong>{mRow.behavior_limit ?? 0}</strong></div>
                                </div>
                                <button type="button" className={`rk-plan-cta ${featured ? 'featured' : ''}`} disabled={isCheckingPlan || busy} onClick={() => startTrialCheckout(mRow)}>{t('start_free_trial')}</button>
                                <div className="rk-plan-div" />
                                <div className="rk-plan-sec"><span>{t('billing_section_apps')}</span></div>
                                <LogoRow items={APPS} lead={APP_ANY} />
                                <div className="rk-plan-div" />
                                <div className="rk-plan-sec"><span>{t('billing_section_channels')}</span></div>
                                <LogoRow items={CHANNELS} lead={CHANNEL_ANY} />
                                <div className="rk-plan-div" />
                                <ul className="rk-plan-feats">
                                  <li><FontAwesomeIcon icon={faCheck} className="rk-feat-ok" /> {t('wxo_price_feat_lead')}</li>
                                  <li className={rescrape !== 'none' ? '' : 'off'}><FontAwesomeIcon icon={rescrape !== 'none' ? faCheck : faMinus} className={rescrape !== 'none' ? 'rk-feat-ok' : ''} /> {rescrape === 'daily' ? t('wxo_price_feat_rescrape_daily') : rescrape === 'monthly' ? t('wxo_price_feat_rescrape_monthly') : t('wxo_price_feat_rescrape')}</li>
                                  <li className={removeBranding ? '' : 'off'}><FontAwesomeIcon icon={removeBranding ? faCheck : faMinus} className={removeBranding ? 'rk-feat-ok' : ''} /> {t('wxo_price_feat_branding')}</li>
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {err && <div className="wxo-err">{err}</div>}
                      <p className="wxo-price-note">{t('cancel_note')}</p>
                      <p className="wxo-price-free"><button type="button" onClick={finish} disabled={busy}>{t('wxo_price_free_link')}</button></p>
                    </>
                  )}
                </div>
              )}

              {stageCta && (
                <div className="wxo-stage-cta">
                  <button type="button" className="wxo-cta-below" onClick={stageCta.onClick} disabled={busy || stageCta.busy}>
                    <span>{stageCta.label}</span>
                    {busy || stageCta.busy ? <span className="wxo-spin" /> : <FontAwesomeIcon icon={faArrowRight} />}
                  </button>
                </div>
              )}
              {key === 'website' && !websiteLive && (
                <p className="wap-below-link"><button type="button" onClick={goNext} disabled={busy || Boolean(websitePhase)}>{t('wap_website_not_now')}</button></p>
              )}
            </>
          )}
        </div>
      </div>

      <UploadFileModal open={docModalOpen} onClose={() => setDocModalOpen(false)} onSubmit={handleDocsAdded} />

      {liveModalOpen && (
        <div className="wxo-live-modal-overlay" onClick={() => setLiveModalOpen(false)}>
          <div className="wxo-live-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="wxo-skills-grid-bg" aria-hidden="true" />
            <div className="wxo-skills-glow" aria-hidden="true" />
            <button type="button" className="wxo-live-modal-close" onClick={() => setLiveModalOpen(false)} aria-label={t('close')}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <div className="wxo-live-modal-inner">
              <span className="wxo-live-modal-icon"><FontAwesomeIcon icon={faHeadset} /></span>
              <h3 className="wxo-live-modal-title">{t('wxo_live_modal_title')}</h3>
              <p className="wxo-live-modal-body">{t('wxo_live_modal_body')}</p>
              <button type="button" className="wxo-live-modal-ok" onClick={() => setLiveModalOpen(false)}>{t('wxo_live_modal_ok')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
